import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as LabelPrimitive from '@radix-ui/react-label';
import { createContext, forwardRef, useContext, useId, } from 'react';
import { cn } from '../lib/cn.js';
const FieldContext = createContext(null);
/**
 * Field context, for the control modules in this package.
 *
 * Exported from the module but deliberately absent from the barrel: a control
 * outside `@ibe/ui` has no business reading this, and the way to add one is to
 * add it here where the wiring is already correct.
 */
export function useField() {
    const context = useContext(FieldContext);
    if (context === null) {
        throw new Error('Field parts must be rendered inside a <Field>');
    }
    return context;
}
export function Field({ children, className, error, description, required = false, }) {
    const id = useId();
    return (_jsx(FieldContext.Provider, { value: {
            controlId: `${id}-control`,
            descriptionId: `${id}-description`,
            errorId: `${id}-error`,
            hasError: error !== undefined && error !== '',
            hasDescription: description !== undefined && description !== '',
            required,
        }, children: _jsxs("div", { className: cn('space-y-1.5', className), children: [children, description !== undefined && description !== '' ? (_jsx(FieldDescription, { children: description })) : null, error !== undefined && error !== '' ? _jsx(FieldError, { children: error }) : null] }) }));
}
/**
 * The required marker's styling, as one class.
 *
 * Exported so a label rendered outside `Field` — a table header for a required
 * cell, a fieldset legend — can wear the same mark rather than approximating
 * it. It is only the styling: the marker's presence is still decided by
 * `Field`'s `required` prop, because that prop also sets `aria-required` on the
 * control and the two must not be separately settable.
 */
export const requiredMarkClass = 'mr-0.5 text-destructive';
export function FieldLabel({ children, className }) {
    const field = useField();
    return (_jsxs(LabelPrimitive.Root, { htmlFor: field.controlId, className: cn('text-sm font-medium leading-none', className), children: [field.required ? (_jsx("span", { "aria-hidden": "true", className: requiredMarkClass, children: "*" })) : null, children] }));
}
/** Shared by every control in this package. Not part of the public surface. */
export function describedBy(field) {
    const ids = [
        field.hasDescription ? field.descriptionId : null,
        field.hasError ? field.errorId : null,
    ].filter((value) => value !== null);
    return ids.length > 0 ? ids.join(' ') : undefined;
}
/** The one control skin. Every field control wears it, so they cannot drift. */
export const controlClasses = 'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive';
export const FieldInput = forwardRef(function FieldInput({ className, ...props }, ref) {
    const field = useField();
    return (_jsx("input", { ref: ref, id: field.controlId, "aria-invalid": field.hasError || undefined, "aria-required": field.required || undefined, "aria-describedby": describedBy(field), className: cn(controlClasses, className), ...props }));
});
export const FieldTextarea = forwardRef(function FieldTextarea({ className, ...props }, ref) {
    const field = useField();
    return (_jsx("textarea", { ref: ref, id: field.controlId, "aria-invalid": field.hasError || undefined, "aria-required": field.required || undefined, "aria-describedby": describedBy(field), className: cn(controlClasses, 'min-h-20', className), ...props }));
});
export function FieldDescription({ children, }) {
    const field = useField();
    return (_jsx("p", { id: field.descriptionId, className: "text-sm text-muted-foreground", children: children }));
}
export function FieldError({ children }) {
    const field = useField();
    // `role="alert"` rather than a plain paragraph: a validation message that
    // appears after a failed submit is new information the user did not ask
    // for, and without a live region it is silent.
    return (_jsx("p", { id: field.errorId, role: "alert", className: "text-sm font-medium text-destructive", children: children }));
}
//# sourceMappingURL=field.js.map